import { useCallback, useMemo, useState } from "react";
import { getPurchaseCatalogCostPrice } from "../utils/purchaseCatalogPricing";

/**
 * Cart lines keyed by admin_product_id (aggregated catalog per product).
 * Insufficient warehouse qty is allowed with a warning (admin Create PO oversell).
 */
export function useCreatePurchaseOrderCart() {
  const [linesByProductId, setLinesByProductId] = useState({});

  const lines = useMemo(
    () => Object.values(linesByProductId),
    [linesByProductId],
  );

  const lineCount = useMemo(
    () => lines.reduce((sum, l) => sum + (l.quantity || 0), 0),
    [lines],
  );

  const estimatedSubtotal = useMemo(
    () =>
      lines.reduce(
        (sum, l) => sum + (Number(l.unit_price) || 0) * (l.quantity || 0),
        0,
      ),
    [lines],
  );

  const stockWarnings = useMemo(
    () =>
      lines
        .filter((l) => (l.quantity || 0) > (Number(l.available_quantity) || 0))
        .map((l) => ({
          admin_product_id: l.admin_product_id,
          product_name: l.product_name,
          sku: l.sku,
          quantity: l.quantity,
          available_quantity: Number(l.available_quantity) || 0,
        })),
    [lines],
  );

  const fulfillmentWarnings = useMemo(
    () =>
      lines.flatMap((l) =>
        (l.warnings || []).map((message) => ({
          admin_product_id: l.admin_product_id,
          product_name: l.product_name,
          sku: l.sku,
          message,
        })),
      ),
    [lines],
  );

  const addOrUpdateFromCatalogRow = useCallback((catalogRow, quantity) => {
    const productId = String(
      catalogRow.admin_product_id || catalogRow.id || "",
    );
    const qty = Math.floor(Number(quantity));
    if (!productId || !Number.isFinite(qty) || qty < 1) {
      return { ok: false, message: "Quantity must be at least 1." };
    }
    const available = Math.max(0, Number(catalogRow.available_quantity) || 0);
    const unitPrice = getPurchaseCatalogCostPrice(catalogRow);
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      return {
        ok: false,
        needsPricing: true,
        message: "Price config is required for this product.",
      };
    }
    if (catalogRow.needs_pricing) {
      return {
        ok: false,
        needsPricing: true,
        message: "Price config is required for this product.",
      };
    }

    setLinesByProductId((prev) => ({
      ...prev,
      [productId]: {
        admin_product_id: productId,
        product_name: catalogRow.product_name,
        sku: catalogRow.sku,
        category_name: catalogRow.category_name,
        images: catalogRow.images,
        unit_price: unitPrice,
        mrp_price: catalogRow.unit_price,
        available_quantity: available,
        delivery_estimate: catalogRow.delivery_estimate ?? null,
        quantity: qty,
        inventory_id: catalogRow.inventory_id || catalogRow.id || undefined,
        outside_mapped_location: Boolean(catalogRow.outside_mapped_location),
        warnings: Array.isArray(catalogRow.warnings) ? catalogRow.warnings : [],
      },
    }));

    const shortage = qty > available;
    const extraWarnings = Array.isArray(catalogRow.warnings)
      ? catalogRow.warnings.filter(Boolean)
      : [];
    return {
      ok: true,
      warning: shortage
        ? `Only ${available} available; ordering ${qty}. Order can still be created.`
        : extraWarnings[0],
      warnings: extraWarnings,
    };
  }, []);

  const updateLineQty = useCallback((productId, rawQty) => {
    setLinesByProductId((prev) => {
      const line = prev[productId];
      if (!line) return prev;
      const qty = Math.floor(Number(rawQty));
      if (!Number.isFinite(qty) || qty < 1) {
        return prev;
      }
      return { ...prev, [productId]: { ...line, quantity: qty } };
    });
  }, []);

  const updateLinePricing = useCallback((productId, { unit_price, mrp_price }) => {
    setLinesByProductId((prev) => {
      const line = prev[productId];
      if (!line) return prev;
      return {
        ...prev,
        [productId]: {
          ...line,
          unit_price: Number(unit_price),
          mrp_price: Number(mrp_price),
        },
      };
    });
  }, []);

  const removeLine = useCallback((productId) => {
    setLinesByProductId((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setLinesByProductId({});
  }, []);

  const getLineForProduct = useCallback(
    (productId) => linesByProductId[productId] || null,
    [linesByProductId],
  );

  const toSubmitItems = useCallback(() => {
    return lines.map((row) => ({
      product_id: row.admin_product_id,
      quantity: row.quantity,
      unit_price: row.unit_price,
      original_unit_price:
        row.mrp_price != null ? Number(row.mrp_price) : undefined,
      total_price: row.unit_price * row.quantity,
      ...(row.inventory_id ? { inventory_id: row.inventory_id } : {}),
    }));
  }, [lines]);

  return {
    lines,
    lineCount,
    estimatedSubtotal,
    stockWarnings,
    fulfillmentWarnings,
    addOrUpdateFromCatalogRow,
    updateLineQty,
    updateLinePricing,
    removeLine,
    clearCart,
    getLineForProduct,
    /** @deprecated use getLineForProduct */
    getLineForLot: getLineForProduct,
    toSubmitItems,
  };
}
